import { useRef, type TouchEvent } from 'react';

/**
 * **2026-05-25** — Hook generico per swipe navigation orizzontale su mobile.
 * Estratto dal pattern già stabilito in `<MobileAsideDrawer>` (FI-77) per
 * riusabilità (caso primo uso: `<ClassModal>` swipe destra/sinistra cicla
 * tra le 12 classi).
 *
 * **Caratteristiche**:
 *  - Distance threshold (default 60px) — swipe deciso da gesto continuo.
 *  - Velocity threshold (default 0.4 px/ms, coerente con `vaul`/Material 3)
 *    come fallback per flick rapidi anche con piccola distanza.
 *  - Min distance per velocity (default 15px) — anti-jitter / anti-tap.
 *  - Horizontal ratio threshold (default 1.5) — `|dx| > |dy| * 1.5` per
 *    distinguere swipe orizzontale da scroll verticale; senza questo
 *    check, uno scroll verticale lento con leggera deriva orizzontale
 *    triggererebbe lo swipe.
 *
 * **Convenzione direzioni**:
 *  - `onSwipeRight` invocato quando il dito si muove verso destra (`dx > 0`).
 *  - `onSwipeLeft` invocato quando il dito si muove verso sinistra (`dx < 0`).
 *  - Mapping prev/next dipende dal caller (es. ClassModal: destra=next, sinistra=prev).
 *
 * **Decisione no-preventDefault**: l'hook NON chiama `e.preventDefault()`
 * in `onTouchMove` (richiederebbe `{ passive: false }` con listener nativi
 * imperativi). La decisione swipe vs scroll viene presa a `onTouchEnd`
 * sulla base del rapporto `|dx|/|dy|`. Conseguenza: lo scroll verticale
 * funziona normalmente; solo gesti chiaramente orizzontali (ratio > 1.5)
 * triggerano lo swipe.
 */
interface SwipeNavigationOptions {
  /** Invocato quando il dito si muove verso destra. */
  onSwipeRight?: (() => void) | undefined;
  /** Invocato quando il dito si muove verso sinistra. */
  onSwipeLeft?: (() => void) | undefined;
  /**
   * Invocato continuamente durante `onTouchMove` con il delta orizzontale `dx`
   * corrente (positivo = dito verso destra, negativo = sinistra). Utile al
   * consumer per pilotare un drag visivo (es. `transform: translateX(dx)`)
   * mentre lo swipe è in corso. Emette `dx = 0` quando il gesto è dominato
   * dall'asse verticale (anti-scroll). Non viene invocato a `onTouchEnd`:
   * il consumer ascolta i callback `onSwipeRight/Left` (swipe completato)
   * oppure usa il proprio reset post-rilascio.
   */
  onSwipeMove?: ((dx: number) => void) | undefined;
  /** Distanza minima in px per uno swipe valido. Default 60. */
  distanceThresholdPx?: number;
  /** Velocity minima px/ms (fallback per flick rapidi). Default 0.4. */
  velocityThresholdPxPerMs?: number;
  /** Distanza minima per cui la velocity conta (anti-jitter). Default 15. */
  minDistanceForVelocityPx?: number;
  /** Ratio min `|dx|/|dy|` per considerare lo swipe orizzontale. Default 1.5. */
  horizontalRatioThreshold?: number;
}

interface SwipeNavigationHandlers {
  onTouchStart: (e: TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: TouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: TouchEvent<HTMLElement>) => void;
  onTouchCancel: (e: TouchEvent<HTMLElement>) => void;
}

const DEFAULT_DISTANCE_PX = 60;
const DEFAULT_VELOCITY_PX_PER_MS = 0.4;
const DEFAULT_MIN_DISTANCE_VELOCITY_PX = 15;
const DEFAULT_HORIZONTAL_RATIO = 1.5;

export function useSwipeNavigation(options: SwipeNavigationOptions): SwipeNavigationHandlers {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeMove,
    distanceThresholdPx = DEFAULT_DISTANCE_PX,
    velocityThresholdPxPerMs = DEFAULT_VELOCITY_PX_PER_MS,
    minDistanceForVelocityPx = DEFAULT_MIN_DISTANCE_VELOCITY_PX,
    horizontalRatioThreshold = DEFAULT_HORIZONTAL_RATIO,
  } = options;

  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const startTime = useRef<number | null>(null);
  const lastX = useRef<number | null>(null);
  const lastY = useRef<number | null>(null);

  const onTouchStart = (e: TouchEvent<HTMLElement>): void => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startTime.current = Date.now();
    lastX.current = touch.clientX;
    lastY.current = touch.clientY;
  };

  const onTouchMove = (e: TouchEvent<HTMLElement>): void => {
    if (startX.current === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    lastX.current = touch.clientX;
    lastY.current = touch.clientY;
    if (onSwipeMove) {
      const dx = touch.clientX - startX.current;
      const dy = startY.current !== null ? touch.clientY - startY.current : 0;
      // Emette `dx` solo se gesto chiaramente orizzontale. Per scroll verticali
      // (dy dominante) emette 0 → consumer non triggera drag visivo durante lo
      // scroll dei contenuti del pannello.
      const isHorizontal =
        Math.abs(dy) === 0 || Math.abs(dx) / Math.abs(dy) >= horizontalRatioThreshold;
      onSwipeMove(isHorizontal ? dx : 0);
    }
  };

  const reset = (): void => {
    startX.current = null;
    startY.current = null;
    startTime.current = null;
    lastX.current = null;
    lastY.current = null;
  };

  const onTouchEnd = (_e: TouchEvent<HTMLElement>): void => {
    const decision = decideSwipe({
      startX: startX.current,
      startY: startY.current,
      startTime: startTime.current,
      lastX: lastX.current,
      lastY: lastY.current,
      distanceThresholdPx,
      velocityThresholdPxPerMs,
      minDistanceForVelocityPx,
      horizontalRatioThreshold,
    });
    if (decision === 'right') onSwipeRight?.();
    else if (decision === 'left') onSwipeLeft?.();
    reset();
  };

  const onTouchCancel = (_e: TouchEvent<HTMLElement>): void => {
    reset();
  };

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel };
}

/**
 * Pure helper estratto da `onTouchEnd` per rispettare il limite di complessità
 * ciclomatica ESLint (`complexity: 10`). Decide la direzione dello swipe (o
 * nessuna) date le coordinate iniziali/finali + le soglie configurate.
 */
interface SwipeDecisionInput {
  startX: number | null;
  startY: number | null;
  startTime: number | null;
  lastX: number | null;
  lastY: number | null;
  distanceThresholdPx: number;
  velocityThresholdPxPerMs: number;
  minDistanceForVelocityPx: number;
  horizontalRatioThreshold: number;
}

type SwipeDecision = 'left' | 'right' | null;

function decideSwipe(input: SwipeDecisionInput): SwipeDecision {
  const delta = computeDelta(input);
  if (delta === null) return null;
  if (!isHorizontalSwipe(delta, input.horizontalRatioThreshold)) return null;
  if (!meetsSwipeThreshold(delta, input)) return null;
  if (delta.dx > 0) return 'right';
  if (delta.dx < 0) return 'left';
  return null;
}

interface SwipeDelta {
  dx: number;
  dy: number;
  absDx: number;
  absDy: number;
  elapsedMs: number;
}

function computeDelta(input: SwipeDecisionInput): SwipeDelta | null {
  if (input.startX === null || input.lastX === null) return null;
  const dx = input.lastX - input.startX;
  const dy = input.lastY !== null && input.startY !== null ? input.lastY - input.startY : 0;
  const elapsedMs = input.startTime !== null ? Date.now() - input.startTime : 0;
  return { dx, dy, absDx: Math.abs(dx), absDy: Math.abs(dy), elapsedMs };
}

function isHorizontalSwipe(delta: SwipeDelta, ratioThreshold: number): boolean {
  if (delta.absDy === 0) return true;
  return delta.absDx / delta.absDy >= ratioThreshold;
}

function meetsSwipeThreshold(delta: SwipeDelta, input: SwipeDecisionInput): boolean {
  const velocity = delta.elapsedMs > 0 ? delta.absDx / delta.elapsedMs : 0;
  const meetsDistance = delta.absDx >= input.distanceThresholdPx;
  const meetsVelocity =
    delta.absDx >= input.minDistanceForVelocityPx && velocity >= input.velocityThresholdPxPerMs;
  return meetsDistance || meetsVelocity;
}
